/**
 * THE FALLBACK SET — no longer the source of truth.
 *
 * Which order fields Change All offers now lives in `field_definitions`
 * (`view_key` + `is_change_all`, migration 467) and is read through
 * `useChangeAllFields`, so an admin opens or closes a field in Data Management
 * instead of waiting for a release. This constant is what that hook falls back to
 * when the registry cannot answer: the first render, a database the migration has
 * not reached yet, or a failed request. Migration 467 seeds `is_change_all = 1` for
 * exactly the fields below, so the fallback and the registry agree on day one and
 * the fallback stays a faithful "what it was before" rather than drifting into a
 * second, stale opinion.
 *
 * DO NOT edit this list to change what Change All offers — flip the flag in Data
 * Management. Edit it only when a field is ADDED to or REMOVED from the order form
 * itself, which is a release anyway. Adding one means three things together: the
 * `bulkFieldName` prop at the render site, an `ORDER_FIELD_COLUMNS` entry so the
 * value reaches the save, and a `field_definitions` row carrying its `view_key` —
 * `test_change_all_order_fields.py` fails if the first two disagree with each other,
 * and the Field Manager shows a form field with no registry row.
 *
 * Auto-stamped side-effect fields (reuseStatusUser/Datetime, *ApprovedBy,
 * availToShip*, …) are absent and can never be added: the server refuses them
 * whatever the flag says (`change_all_order_fields._NEVER_CHANGE_ALL`), because
 * copying a stamp across a batch forges the record of an act rather than repeating
 * it. (masterPro/moveType were previously excluded but are now intentionally
 * fannable per LC request.)
 *
 * EXCEPTION: `deleted` (bulk delete) HAS its own wired checkbox
 * (`bulkFieldName="addlInfoDeleted"` in AddlInfoView) but is intentionally kept OUT
 * of this set and out of the registry flag — a destructive delete must be armed by
 * its explicit checkbox and reason modal, never swept in by "flag all edited". The
 * endpoint still accepts the column (`ENFORCEMENT_EXTRA`), which is what makes bulk
 * delete work at all. Its checkbox is drawn via REGISTRY_EXEMPT_FIELDS below.
 */
export const CHANGE_ALL_ELIGIBLE_FIELDS: ReadonlySet<string> = new Set([
  // Order Info (61)
  'csr', 'whseCsrUsername', 'dispatcher', 'carrierCode', 'chassisId', 'scac',
  'returnPronumber', 'returnEquipment', 'returnChassis', 'rail',
  'dropReleaseDatetime', 'dropReleaseComment', 'bobtailApproved',
  'pieceCount', 'commodity',
  'purchaseOrder', 'tareLbs', 'sealNumber', 'portOfOriginCode', 'portOfDischargeCode',
  'custTargetDate', 'isDaycab', 'scale', 'otr', 'isTank', 'hazmat', 'isDrop',
  'railbilled', 'targetOnOrBy', 'targetDeliveryDate', 'size', 'equipType',
  'isGwiEquip', 'container', 'flexBooking', 'chassisProvider', 'chassisType',
  'lbs', 'kgs', 'metricTons', 'comments',
  // recently-opened LC fields (docs/field-fe-validation.md) + CST Dispatcher
  'cstDispatcherUsername', 'bookingNumber', 'hot', 'swing', 'lastFreeDay',
  'firstRcv', 'documentCutoffDatetime', 'cutoffDatetime', 'customerCutoff',
  'rolling', 'rollingFault', 'vessel', 'voyage', 'tmwOrderNum', 'releasedSet',
  'foodGrade', 'offhire', 'equipReposition', 'etaDate',
  // Addl Info (47)
  'consigneeCode', 'customer', 'brokerCode', 'dcNumber', 'dept', 'packaged',
  'equipHold', 'allowBrokerage', 'streetTurnHold', 'isExam', 'examiner',
  'inbondType', 'inbondNumber', 'renderedDeadlineDate', 'renderedDate',
  'sendEmailNotifications', 'reuseOrigCode', 'reuseStatus',
  // recently-opened LC fields (docs/field-fe-validation.md)
  'brokerContactRecno', 'priority', 'priority2', 'dcDueDate', 'fullEmptyDate',
  'yardLocation', 'demurrageReason', 'examNo', 'examReleaseDate',
  'inbondResponsible', 'inbondRespOther', 'inbondTenderRcvd', 'whseEntryNum',
  'whse3461Date', 'whseRedStampDate', 'zeroRecordDate', 'temperatureControlled',
  'genset', 'temperature', 'tempScale', 'ventSetting', 'highValueCargo',
  'cargoValue',
  // additionally opted in per LC request (incl. structural masterPro/moveType)
  'yardReportDatetime', 'notOnYardReportDate', 'templateType', 'gwiMove',
  'masterPro', 'moveType',
  // Tracing (31)
  'traceRunDatetime', 'traceRunCarrier', 'traceEventDatetime', 'traceEvent',
  'traceLocation', 'traceMode', 'availableGoodToGoDatetime', 'traceTerminateDatetime',
  'revisedEtaDatetime', 'customsStatus', 'lineStatus', 'outGateDatetime', 'otherHolds',
  // recently-opened LC fields (docs/field-fe-validation.md)
  'traceVessel', 'traceVoyage', 'vesselSailDateEst', 'vesselSailDatetime',
  'vesselArrivalDateEst', 'vesselArrivalDatetime', 'arrivalGroundedDatetime',
  'arrivalGroundedLocation', 'bookingOnFileDate', 'equipReleaseDate',
  'preAdviseDatetime', 'railBillStatus', 'trainDepartDatetime', 'customsRelDatetime',
  'schedulingContactType', 'schedulingContactInfo', 'railPickupContactType',
  'railPickupContactInfo',
  // Addl Cost (4) — per-diem order fields, editable via perDiemEditable
  'perDiemStartDate', 'perDiemEndDate', 'perDiemContractNumber', 'perDiemApproval',
]);

/**
 * Checkboxes the registry does NOT govern — always drawn when change-all mode is on.
 *
 * The frontend counterpart of `change_all_order_fields.ENFORCEMENT_EXTRA`, and it
 * exists for the same single field. Bulk delete is a real feature reached through the
 * order arm, but `deleted` is deliberately in no eligibility list anywhere: it must
 * never be swept in by "flag all edited", so it carries its own checkbox plus a reason
 * modal instead. Gating that checkbox on `is_change_all` would mean the only way to
 * restore bulk delete is to flag a column that must never be flagged.
 *
 * Deliberately NOT a general escape hatch. Anything added here becomes a field an
 * admin cannot turn off in Data Management, which is the opposite of why the registry
 * exists — so it takes a reason as specific as this one.
 */
export const REGISTRY_EXEMPT_FIELDS: ReadonlySet<string> = new Set(['addlInfoDeleted']);
